import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape CFG-02 publishes, imported rather than restated.
import type { FeatureSummary } from "@tdelivr/api-client/generated/configuration/feature-management.js";

import { boundedNote, PER_FETCH, type BoardRow } from "../board.ts";
import { filterFlags, flagRow, FLAG_STATES } from "../flags.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * Feature flags: which capabilities are on, for everyone or for a share of customers.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `flags`, against CFG-02 `GET /v1/features`.
 *
 * **This page was blocked twice over and ADR 0045 closed both.** Every operation of CFG-02 was
 * under `/internal/v1`, which ADR 0029 §2 says the gateway refuses at load rather than rewrites;
 * and the one read among them took a `flagKey`, which is the identifier a board of a tenant's flags
 * exists to discover. A public surface alone would have left the second fault standing, which is
 * why what was published is a listing.
 *
 * **What the listing withholds is the reason it is a summary.** `allocationSeed` is the value
 * CFG-02 hashes with a subject key to decide a percentage rollout, so a browser holding it could
 * compute which customers a 25% rollout includes before it reaches them. The two approving
 * subjects, the approval reference, the change reason and the concurrency etag are withheld beside
 * it. None of the five is drawn by this board.
 *
 * **No write requirement, and the design's own primary action is why.** Its form is
 * `New feature flag → Create flag`, and CFG-02's publication command requires a `requestedBy` that
 * must differ from the approving actor — the service refuses a proposal whose requester is the
 * caller, by `separation_of_duties_required` — plus an `approvalReference` matching
 * `urn:tdelivr:approval:…`, which nothing on this platform mints. A single administrator pressing a
 * button cannot satisfy either, and the design's form has neither field. The command is also not
 * published under `/v1` at all, deliberately: ADR 0045 publishes the read and nothing else.
 *
 * **`Expires` has no field anywhere.** That is one of the design's four tiles, one of its four
 * badge words and one of its four form inputs. Q-151, and `flags.ts` records it beside the rest.
 */

const featuresApi = services.configuration__feature_management;

export interface FeatureFlagsProps {
  readonly transport: Transport;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly features: readonly FeatureSummary[];
  readonly truncated: boolean;
}

export function FeatureFlags({ transport, crumbs }: FeatureFlagsProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    const client = featuresApi.createFeatureManagementClient(transport);
    try {
      const answered = await client.listTenantFeatureSummaries(PER_FETCH, undefined);
      setLoaded({
        features: answered.features,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["feature-management:summary:read"]));
      setLoaded(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: readonly BoardRow[] | undefined = useMemo(() => {
    if (loaded === undefined) return undefined;
    return filterFlags(loaded.features, state).map(flagRow);
  }, [loaded, state]);

  const bounded = boundedNote(
    loaded?.features.length,
    loaded?.truncated ?? false,
    "flags",
    "ordered by key",
  );

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Feature flags"
      /*
       * The design's chip reads `CFG-02`, and for once that is the service this page reads. Its
       * `templates` and `integrations` neighbours both name services that hold nothing the page is
       * a list of; this one is exact.
       */
      source="CFG-02"
      description="Turn capabilities on and off, for everyone or a share of customers."
      columns={["Flag", "Audience", "Rollout"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="Flags appear here as soon as somebody publishes a version of one for this tenant."
      primary={{
        label: "New flag",
        /*
         * Drawn, and refused. Three separate reasons and any one of them alone would be enough,
         * which is why the refusal names the first two rather than the shortest.
         */
        refusal:
          "CFG-02 refuses a flag version whose requester is the approving actor, and requires an approval reference matching urn:tdelivr:approval:… that nothing on this platform mints — so one administrator cannot publish a flag, and the design's form has neither field. The publication command is also not published under /v1: ADR 0045 publishes the read and nothing else.",
      }}
      filter={{
        unfiltered: "All states",
        options: FLAG_STATES.map((value) => ({ value, label: value })),
        value: state,
        onChange: setState,
      }}
      note={{
        /*
         * The design's `info` note is kept word for word, because both halves of it are true of
         * CFG-02 and that is unusual on this console.
         *
         * "Flags change behaviour without a release" — a publication writes a new immutable version
         * and services read the current one, with no deployment involved. "every change is audited"
         * — `protected_audit_log` is append-only behind a trigger that refuses an update or a
         * delete, and the publication records the actor, the correlation and the approval reference.
         */
        kind: "info",
        glyph: "info",
        text: "Flags change behaviour without a release; every change is audited.",
      }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote="Every flag this tenant holds, at its newest published version. A rollout of 0% is not off: with no percentage rollout the default decides, and a targeting rule is answered before the rollout is consulted at all."
      exportName="feature-flags"
      detailSubtitle="Feature flags · CFG-02"
      detailNote="Flags change behaviour without a release; every change is audited."
    />
  );
}
